import CloseIcon from '@mui/icons-material/Close';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import { PreferencesContent } from 'app/pages/EmailsGridPage/components/PreferencesContent';
import type {
  SegmentRow,
  SegmentVehicleItem,
} from 'app/pages/EmailsGridPage/apiService/emailsGridApiService';
import type { VehicleListKind } from 'app/pages/EmailsGridPage/hooks/useSegmentInventories';
import type { TestTemplateGenerationStep } from 'app/pages/TestNurturingPage/hooks/useTestNurturingPage';
import TestTemplatePreferencesSummary from 'app/pages/TestNurturingPage/components/TestTemplatePreferencesSummary';

export type TestTemplateGenerationModalProps = {
  open: boolean;
  onClose: () => void;
  step: TestTemplateGenerationStep;
  /** Step 1 → save preferences (PUT) + advance to step 2. */
  onNext: () => void | Promise<void>;
  /** Step 2 → go back to step 1 (preferences still editable). */
  onBack: () => void;
  /** Step 2 → dispatch generate (same as EmailsGridPage) + close modal. */
  onGenerate: () => void | Promise<void>;
  /** Disabled state for buttons (save in-progress or generate already running). */
  saveLoading: boolean;
  /** Whether there's a valid preference_id from GET — gates Next/Generate when needed. */
  canSavePreferences: boolean;
  /** Preferences data — wired through from `useSegmentInventories`. */
  segmentRows: SegmentRow[];
  headerRow: string[];
  loading: boolean;
  error: string | null;
  expandedCell: { segmentName: string; kind: VehicleListKind } | null;
  vehiclesForExpandedCell: SegmentVehicleItem[];
  vehiclesLoading: boolean;
  vehiclesError: string | null;
  preferredVinsForExpandedCell: string[];
  missingVinsForExpandedCell: string[];
  onTogglePreferredVin: (
    segmentName: string,
    kind: VehicleListKind,
    vin: string,
    selected: boolean,
  ) => void;
  onToggleExpanded: (segmentName: string, kind: VehicleListKind) => void;
  /** Selected VINs map per segment+kind — drives the step 2 read-only confirmation view. */
  preferredVinsSnapshot: Record<string, string[]>;
  /** Cached vehicle details per segment+kind used to render rich mini-cards on step 2. */
  vehiclesBySegmentKind: Record<string, SegmentVehicleItem[]>;
  /** True while the consumer hook batch-fetches missing segment inventories for step 2. */
  prefetchLoading: boolean;
};

const STEP_CIRCLE_BASE_SX = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.95rem',
};

export default function TestTemplateGenerationModal({
  open,
  onClose,
  step,
  onNext,
  onBack,
  onGenerate,
  saveLoading,
  canSavePreferences,
  segmentRows,
  headerRow,
  loading,
  error,
  expandedCell,
  vehiclesForExpandedCell,
  vehiclesLoading,
  vehiclesError,
  preferredVinsForExpandedCell,
  missingVinsForExpandedCell,
  onTogglePreferredVin,
  onToggleExpanded,
  preferredVinsSnapshot,
  vehiclesBySegmentKind,
  prefetchLoading,
}: TestTemplateGenerationModalProps) {
  const isStep1 = step === 1;
  const isStep2 = step === 2;
  // Step 1's Next button is always enabled (when not actively saving). If there are no
  // unsaved changes we just advance without calling the API. Disabling here would block
  // the user from progressing when their existing preferences are already correct.
  const isNextDisabled = saveLoading;
  const isGenerateDisabled = saveLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
      aria-labelledby="test-template-generation-dialog-title"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 1,
        }}
      >
        <Typography
          id="test-template-generation-dialog-title"
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, color: 'primary.dark', pr: 1 }}
        >
          Test Template Generation
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', mt: -0.5 }}
          disabled={saveLoading}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          pt: 0,
          pb: 2,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            mb: 3,
            mt: 0.5,
            flexShrink: 0,
          }}
        >
          <Box
            component={isStep2 ? 'button' : 'div'}
            type={isStep2 ? 'button' : undefined}
            onClick={isStep2 && !saveLoading ? onBack : undefined}
            disabled={isStep2 ? saveLoading : undefined}
            aria-label={isStep2 ? 'Back to step 1' : undefined}
            sx={{
              ...STEP_CIRCLE_BASE_SX,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              border: 'none',
              p: 0,
              font: 'inherit',
              cursor: isStep2 && !saveLoading ? 'pointer' : 'default',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              ...(isStep2 && {
                '&:hover': {
                  transform: !saveLoading ? 'scale(1.05)' : 'none',
                  boxShadow: !saveLoading ? 2 : 'none',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.dark',
                  outlineOffset: 2,
                },
                '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
              }),
            }}
          >
            1
          </Box>
          <Box
            sx={{
              flex: 1,
              height: 2,
              minWidth: 24,
              bgcolor: isStep2 ? 'primary.main' : 'divider',
              mx: 0.5,
              transition: 'background-color 0.2s ease',
            }}
          />
          <Box
            component={isStep1 ? 'button' : 'div'}
            type={isStep1 ? 'button' : undefined}
            onClick={
              isStep1 && !isNextDisabled
                ? () => {
                    void onNext();
                  }
                : undefined
            }
            disabled={isStep1 ? isNextDisabled : undefined}
            aria-label={isStep1 ? 'Go to step 2' : undefined}
            sx={{
              ...STEP_CIRCLE_BASE_SX,
              bgcolor: isStep2 ? 'primary.main' : 'grey.300',
              color: isStep2 ? 'primary.contrastText' : 'grey.700',
              border: 'none',
              p: 0,
              font: 'inherit',
              cursor: isStep1 && !isNextDisabled ? 'pointer' : 'default',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.2s ease',
              ...(isStep1 && {
                '&:hover': {
                  transform: !isNextDisabled ? 'scale(1.05)' : 'none',
                  boxShadow: !isNextDisabled ? 2 : 'none',
                  bgcolor: !isNextDisabled ? 'grey.400' : 'grey.300',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.dark',
                  outlineOffset: 2,
                },
                '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
              }),
            }}
          >
            2
          </Box>
        </Box>

        <Box sx={{ mb: 1.5, flexShrink: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {isStep1 ? 'Configure preferences' : 'Review preferences & generate'}
          </Typography>
          {isStep2 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Confirm the VINs selected for each segment. Click Back to make
              changes, or Generate to start template generation.
            </Typography>
          )}
        </Box>

        {isStep1 ? (
          <PreferencesContent
            isOpen={open}
            segmentRows={segmentRows}
            headerRow={headerRow}
            loading={loading}
            error={error}
            expandedCell={expandedCell}
            vehiclesForExpandedCell={vehiclesForExpandedCell}
            vehiclesLoading={vehiclesLoading}
            vehiclesError={vehiclesError}
            preferredVinsForExpandedCell={preferredVinsForExpandedCell}
            missingVinsForExpandedCell={missingVinsForExpandedCell}
            onTogglePreferredVin={onTogglePreferredVin}
            onToggleExpanded={onToggleExpanded}
          />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <TestTemplatePreferencesSummary
              segmentRows={segmentRows}
              preferredVinsSnapshot={preferredVinsSnapshot}
              vehiclesBySegmentKind={vehiclesBySegmentKind}
              loading={loading}
              error={error}
              prefetchLoading={prefetchLoading}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 0,
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box>
          {isStep2 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={onBack}
              disabled={saveLoading}
            >
              Back
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {isStep1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                void onNext();
              }}
              disabled={isNextDisabled}
              startIcon={
                saveLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {saveLoading ? 'Saving...' : 'Next'}
            </Button>
          )}
          {isStep2 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                void onGenerate();
              }}
              disabled={isGenerateDisabled}
              startIcon={
                saveLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoAwesome fontSize="small" />
                )
              }
            >
              {saveLoading ? 'Saving...' : 'Generate'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
